import { LinearProgress, Typography } from '@mui/material';

import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useState } from 'react';

import { tss } from 'tss-react/mui';

import { Or2Button } from '@/components/button/Or2Button';

import { riskyPluralize } from '@/utils/strings/riskyPluralize';

import type { FileManagerUploadingState } from '../FileManagerUploadingBar';

interface Props {
  uploadingState: FileManagerUploadingState;
  setUploadingState: Dispatch<SetStateAction<FileManagerUploadingState>>;
}

const useStyles = tss.create(({ theme }) => ({
  root: {
    width: '100%',
    paddingBottom: theme.spacing(2),
  },
  content: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  error: {
    color: theme.palette.error.main,
  },
}));

export const UploadSummary = ({ uploadingState, setUploadingState }: Props) => {
  const [completedFiles, setCompletedFiles] = useState(new Set<number>());
  const [completedZipFiles, setCompletedZipFiles] = useState(new Set<number>());

  const { classes } = useStyles();

  const { uploadingFiles, uploadingFolders, uploadingZipFiles } =
    uploadingState;

  const batchFiles = [...uploadingFiles, ...uploadingZipFiles].filter(
    (file) => {
      if (completedFiles.has(file.id)) {
        return false;
      }

      if (completedZipFiles.has(file.id)) {
        return false;
      }

      return true;
    },
  );

  const batchFilesWithTotalBytes = batchFiles.filter((file) => file.totalBytes);

  const uploadDecimalCompleted = batchFilesWithTotalBytes.length
    ? batchFilesWithTotalBytes.reduce(
        (sum, file) => sum + file.uploadedBytes,
        0,
      ) /
      batchFilesWithTotalBytes.reduce((sum, file) => sum + file.totalBytes, 0)
    : 0;

  // Handles the completion of uploads
  useEffect(() => {
    const uploadingIds = new Set(uploadingFiles.map((file) => file.id));
    const unuploadedFiles = uploadingFiles.filter(
      (file) => file.uploadedBytes === 0,
    );
    const uploadingZipIds = new Set(uploadingZipFiles.map((file) => file.id));
    const unuploadedZipFiles = uploadingZipFiles.filter(
      (file) => file.uploadedBytes === 0,
    );

    // Checks if there's any files that aren't stored as completed yet
    if (
      uploadingZipIds.size !== completedZipFiles.size ||
      uploadingIds.size !== completedFiles.size
    ) {
      return;
    }

    // Checks if there's any files that haven't been uploaded yey
    if (unuploadedFiles.length + unuploadedZipFiles.length === 0) {
      return;
    }

    // Checks if we're done uploading
    if (uploadDecimalCompleted === 1) {
      setCompletedFiles(
        (completedFiles) => new Set([...completedFiles, ...uploadingIds]),
      );
      setCompletedZipFiles(
        (completedZipFiles) =>
          new Set([...completedZipFiles, ...uploadingZipIds]),
      );
    }
  }, [
    completedFiles.size,
    completedZipFiles.size,
    uploadingFiles,
    uploadingZipFiles,
    uploadDecimalCompleted,
  ]);

  const currentlyUploadingFiles = batchFiles.filter(
    (file) => !file.totalBytes || file.uploadedBytes < file.totalBytes,
  );

  const numUploadedFiles = [...uploadingFiles, ...uploadingZipFiles].filter(
    (file) => file.totalBytes === file.uploadedBytes && !file.error,
  ).length;

  const numErrors = [
    ...uploadingFiles,
    ...uploadingFolders,
    ...uploadingZipFiles,
  ].filter((file) => Boolean(file.error)).length;

  const outstandingFolders = uploadingFolders.filter(
    (folder) => !folder.created && !folder.error,
  );

  const processingZips = uploadingZipFiles.filter(
    (file) =>
      !file.error &&
      file.zipStatus &&
      ['creating', 'extracting'].includes(file.zipStatus),
  );

  const isProcessing = outstandingFolders.length + processingZips.length > 0;

  return (
    <div className={classes.root}>
      <div className={classes.content}>
        <Typography>
          {currentlyUploadingFiles.length === 0 ? (
            processingZips.length === 0 ? (
              outstandingFolders.length === 0 ? (
                <>
                  Uploaded {riskyPluralize('file', numUploadedFiles)}
                  {numErrors > 0 && (
                    <span className={classes.error}>
                      {' '}
                      {riskyPluralize('error', numErrors)}
                    </span>
                  )}
                </>
              ) : (
                <>Creating folders</>
              )
            ) : (
              <>
                Processing {riskyPluralize('zip file', processingZips.length)}
              </>
            )
          ) : (
            <>
              Uploading {riskyPluralize('file', currentlyUploadingFiles.length)}
            </>
          )}
        </Typography>
        <div>
          {currentlyUploadingFiles.length === 0 && !isProcessing && (
            <Or2Button
              component="div"
              onClick={(event) => {
                event.stopPropagation();

                setUploadingState({
                  uploadingFiles: [],
                  uploadingFolders: [],
                  uploadingZipFiles: [],
                });
              }}
            >
              Clear
            </Or2Button>
          )}
        </div>
        {currentlyUploadingFiles.length > 0 && (
          <div>
            <Typography variant="caption">
              {Math.floor(uploadDecimalCompleted * 100)}%
            </Typography>
          </div>
        )}
      </div>
      {currentlyUploadingFiles.length > 0 ? (
        <LinearProgress
          variant="determinate"
          value={uploadDecimalCompleted * 100}
        />
      ) : (
        isProcessing && <LinearProgress />
      )}
    </div>
  );
};
